import React, { useMemo, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import FilterListOffRoundedIcon from "@mui/icons-material/FilterListOffRounded";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { useTranslation } from "react-i18next";
import { buildProdutosFilterSummaries } from "../../utils/dashboardProdutosFilterState";
import { getDashboardSavedFilters } from "../../utils/dashboardSavedFilters";
import DashboardSavedFiltersMenu from "./DashboardSavedFiltersMenu";

export default function DashboardProdutosFiltersControl({
  filterState,
  criteriaCount = 0,
  onOpen,
  onClear,
  section,
  onApplySaved,
}) {
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const summaries = useMemo(() => buildProdutosFilterSummaries(filterState, t), [filterState, t]);
  const hasActive = criteriaCount > 0;
  const [savedMenuAnchor, setSavedMenuAnchor] = useState(null);
  const [savedEntries, setSavedEntries] = useState([]);
  const showSavedMenu = Boolean(section && onApplySaved);
  const refreshSavedEntries = () => {
    setSavedEntries(getDashboardSavedFilters(section));
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
        minWidth: 0,
        px: hasActive ? 1 : 0,
        py: hasActive ? 0.5 : 0,
        borderRadius: 2,
        bgcolor: hasActive ? alpha(theme.palette.primary.main, 0.04) : "transparent",
      }}
    >
      <Button
        variant={hasActive ? "contained" : "outlined"}
        size="small"
        color="primary"
        startIcon={<TuneRoundedIcon sx={{ fontSize: 18 }} />}
        onClick={onOpen}
        endIcon={
          hasActive ? (
            <Box
              component="span"
              sx={{
                ml: 0.25,
                minWidth: 20,
                height: 20,
                px: 0.75,
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.7rem",
                fontWeight: 700,
                bgcolor: "rgba(255,255,255,0.22)",
              }}
            >
              {criteriaCount}
            </Box>
          ) : null
        }
        sx={{
          height: 34,
          whiteSpace: "nowrap",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.8125rem",
          px: 1.5,
          flexShrink: 0,
          borderRadius: 2,
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
          "& .MuiButton-endIcon": { ml: 0.75 },
        }}
      >
        {hasActive
          ? t("dashboardPage.produtosFilters.adjust", { defaultValue: "Ajustar filtros" })
          : t("dashboardPage.produtosFilters.open", { defaultValue: "Filtrar produtos" })}
      </Button>

      {hasActive ? (
        <Tooltip
          title={t("dashboardPage.produtosFilters.clearTooltip", {
            defaultValue: "Remover todos os filtros",
          })}
        >
          <IconButton
            size="small"
            onClick={onClear}
            aria-label={t("dashboardPage.produtosFilters.clear", { defaultValue: "Limpar" })}
            sx={{
              flexShrink: 0,
              color: "text.secondary",
              "&:hover": { color: "error.main", bgcolor: alpha(theme.palette.error.main, 0.08) },
            }}
          >
            <FilterListOffRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      ) : null}

      {hasActive && summaries.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flex: 1,
            minWidth: 0,
            overflowX: "auto",
            flexWrap: "nowrap",
            py: 0.25,
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { height: 3 },
          }}
        >
          {summaries.map((item) => (
            <Tooltip key={item.key} title={item.title || item.label} enterDelay={400}>
              <Chip
                size="small"
                label={item.label}
                onClick={onOpen}
                sx={{
                  height: 26,
                  fontSize: "0.75rem",
                  fontWeight: item.tone === "emphasis" ? 600 : 500,
                  flexShrink: 0,
                  borderRadius: 1.5,
                  bgcolor:
                    item.tone === "emphasis"
                      ? alpha(theme.palette.primary.main, 0.08)
                      : alpha(theme.palette.text.primary, 0.04),
                  color: item.tone === "emphasis" ? "primary.main" : "text.primary",
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            </Tooltip>
          ))}
        </Box>
      ) : !hasActive ? (
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          sx={{ display: { xs: "none", md: "block" }, flex: 1, fontSize: "0.8125rem", pl: 0.5 }}
        >
          {t("dashboardPage.produtosFilters.hint", {
            defaultValue: "Refine por categoria, disponibilidade ou busca",
          })}
        </Typography>
      ) : (
        <Box sx={{ flex: 1 }} />
      )}

      {showSavedMenu ? (
        <>
          <Tooltip
            title={t("dashboardPage.savedFilters.title", { defaultValue: "Filtros salvos" })}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                refreshSavedEntries();
                setSavedMenuAnchor(e.currentTarget);
              }}
              aria-label={t("dashboardPage.savedFilters.title", {
                defaultValue: "Filtros salvos",
              })}
              sx={{ flexShrink: 0, color: "text.secondary" }}
            >
              <BookmarkBorderIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <DashboardSavedFiltersMenu
            anchorEl={savedMenuAnchor}
            open={Boolean(savedMenuAnchor)}
            onClose={() => setSavedMenuAnchor(null)}
            section={section}
            entries={savedEntries}
            filterState={filterState}
            criteriaCount={criteriaCount}
            onApply={onApplySaved}
            onSaved={refreshSavedEntries}
          />
        </>
      ) : null}
    </Box>
  );
}
